/*
 *
 * SendDirectlyPopup
 *
 * @version 1.0.0
 * @author Magarelli-Fabrizio
 * @since 21-Jan-2025
 */

import React from 'react'
import PropTypes from 'prop-types'
import { useIntl } from 'react-intl'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import MerchantGenericCard from '@nexidigital/merchantgenericcard'
import MerchantModal from '@nexidigital/merchantmodal'
import PortalPhoneInput from '@nexidigital/portalphoneinput'
import PortalSelect from '@nexidigital/portalselect'
import PortalTextInput from '@nexidigital/portaltextinput'
import Toggle from 'Hoc/ToggleComponent'
import { getLanguage } from 'Utility/globalMethods'
import { handleOnlyNumberKeyDown } from 'Utility/numberMethods'
import { LANGUAGES_ISO639_2_CODE_FORMAT } from 'Schemas/constants'
import prefixOptions from 'Schemas/countries.json'
import formattedMessage, { lang, captions, messages } from './languageModule'
import './SendDirectlyPopup.scss'

const SendDirectlyPopup = ({ actions, result, isLoading }) => {
  const intl = useIntl()
  const { data, type } = result
  const formik = useFormik({
    initialValues: {
      language: data?.language || LANGUAGES_ISO639_2_CODE_FORMAT[getLanguage()],
      phone: '',
      phoneCountryCode: '',
      email: data?.cardHolderEmail || data?.customerInfo?.email || ''
    },
    validationSchema: Yup.object({
      phone: Yup.string().when([], {
        is: () => type === 'sms',
        then: Yup.string()
          .required(intl.formatMessage(captions.isRequired))
          .matches(/^\d+$/, intl.formatMessage(captions.validPhoneNumber))
          .min(6, intl.formatMessage(captions.validPhoneNumber))
          .max(15, intl.formatMessage(captions.validPhoneNumber)),
        otherwise: Yup.string()
      }),
      phoneCountryCode: Yup.string().when([], {
        is: () => type === 'sms',
        then: Yup.string().required(intl.formatMessage(captions.isRequired)),
        otherwise: Yup.string()
      }),
      email: Yup.string().when([], {
        is: () => type === 'email',
        then: Yup.string()
          .required(intl.formatMessage(captions.isRequired))
          .email(intl.formatMessage(captions.validEmail)),
        otherwise: Yup.string()
      })
    })
  })

  const disabledButtonControl = () =>
    type === 'sms' ? !formik.values.phone || !formik.values.phoneCountryCode : !formik.values.email

  const handlePrimaryAction = () => {
    const language = formik.values.language || 'ENG'
    const target = type === 'email' ? formik.values.email : formik.values.phone
    const payload = {
      channel: type.toUpperCase(),
      language: language,
      email: target //TODO: change with phone for sms
    }

    if (type === 'sms') {
      payload.mobilePhoneCountryCode = formik.values.phoneCountryCode
      payload.mobilePhone = formik.values.phone
    }

    if (!payload.language || !payload.email) {
      console.error('Payload non valido: language e email non possono essere null')
      return
    }

    if (result.isBooking) {
      actions.doExecuteBookingAction({
        url: `/reservations/${result.data?.id}/send_link`,
        values: payload,
        action: 'SEND_LINK'
      })
    } else {
      result.data?.onConfirm?.({
        type,
        data: {
          ...formik.values,
          language,
          target
        }
      })
    }
  }
  return (
    <Toggle>
      <MerchantModal
        closeLabel={intl.formatMessage(captions.close)}
        title={type === 'sms' ? formattedMessage.smsTitle : formattedMessage.emailTitle}
        ariaModalTitle={type === 'sms' ? formattedMessage.smsTitle : formattedMessage.emailTitle}
        description={formattedMessage.exportDescription}
        ariaModalDescription={formattedMessage.exportDescription}
        onClose={() => actions.closePopup()}
        primaryButtonLabel={formattedMessage.confirm}
        primaryAction={handlePrimaryAction}
        primaryButtonDisabled={!formik.isValid || disabledButtonControl()}
        primaryButtonLoading={isLoading}>
        <MerchantGenericCard>
          <div className='sendDirectly_card_container'>
            <div className='sendDirectly_card_item'>
              <PortalSelect
                {...formik.getFieldProps('language')}
                id='pbl_sendDirectly_language'
                label={formattedMessage.language}
                ariaLabelIconButton={intl.formatMessage(captions.languageAriaLabel)}
                options={Object.entries(LANGUAGES_ISO639_2_CODE_FORMAT).map(([k, v]) => ({
                  value: v,
                  label: intl.formatMessage(lang[k])
                }))}
                onChange={(e) => formik.setFieldValue('language', e.value)}
                noSearch
                placeholder={intl.formatMessage(captions.languagePlaceholder)}
              />
            </div>

            {type === 'sms' && (
              <PortalPhoneInput
                id={'pbl_sendDirectly_phone'}
                {...formik.getFieldProps('phoneCountryCode')}
                phoneLabel={intl.formatMessage(captions.mobileNumber)}
                pathToDirectFlags={process.env.COUNTRY_FLAGS_URL}
                maxLength={15}
                onPrefixChangeHandler={({ value }) => formik.setFieldValue('phoneCountryCode', value)}
                error={formik?.errors?.phone && formik?.touched?.phone}
                errorText={formik?.errors?.phone}
                selectedPrefix={formik.values.phoneCountryCode}
                phonePrefixes={prefixOptions}
                prefixLabel={intl.formatMessage(captions.countryCode)}
                formik={formik.getFieldProps('phone')}
                prefixPlaceholder={intl.formatMessage(captions.select)}
                placeholder={intl.formatMessage(captions.enterNumber)}
                prefixError={formik?.errors?.phoneCountryCode && formik?.touched?.phoneCountryCode}
                prefixErrorText={formik?.errors?.phoneCountryCode}
                onPrefixBlurHandler={() => formik.setFieldTouched('phoneCountryCode', true)}
                onKeyDown={handleOnlyNumberKeyDown}
              />
            )}
            {type === 'email' && (
              <div className='sendDirectly_card_item'>
                <PortalTextInput
                  ariaLabelIcon={intl.formatMessage(messages.warningAriaLabel)} 
                  id='pbl_sendDirectly_email'
                  label={intl.formatMessage(captions.email)}
                  value={formik.values.email}
                  onChange={(e) => formik.setFieldValue('email', e.target.value)}
                  onBlur={() => formik.setFieldTouched('email', true)}
                  error={formik?.errors?.email && formik?.touched?.email}
                  errorText={formik?.errors?.email}
                  placeholderBlank
                />
              </div>
            )}
          </div>
        </MerchantGenericCard>
      </MerchantModal>
    </Toggle>
  )
}

SendDirectlyPopup.propTypes = {
  actions: PropTypes.object,
  result: PropTypes.object,
  isLoading: PropTypes.bool
}

SendDirectlyPopup.defaultProps = {
  actions: {},
  result: {},
  isLoading: false
}

export default SendDirectlyPopup
